import React, { useEffect, useMemo } from 'react';

import Avatar from '@mui/material/Avatar';
import Button from '@mui/material/Button';
import Checkbox from '@mui/material/Checkbox';
import FormControl from '@mui/material/FormControl';
import FormControlLabel from '@mui/material/FormControlLabel';
import IconButton from '@mui/material/IconButton';
import MenuItem from '@mui/material/MenuItem';
import Paper from '@mui/material/Paper';
import Select from '@mui/material/Select';
import Slider from '@mui/material/Slider';
import Typography from '@mui/material/Typography';
import Box from '@mui/system/Box';

import CloseIcon from '@mui/icons-material/Close';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { DateTimePicker } from '@mui/x-date-pickers/DateTimePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import dayjs, { Dayjs } from 'dayjs';
import { SearchFiltersType } from 'shared/types';
import MapboxGeocoderInput from '../../../../components/common/MapboxGeocoder';
import SkeletonLoader from '../../../../components/common/SkeletonLoader';
import { SERVICE_TYPE_LABELS } from '../../../../happypaws-shared/src/Services/services';
import { useGetPets } from '../../../../hooks/pets/useGetPets';

interface SearchFiltersProps {
  filters: SearchFiltersType;
  isOpen: boolean;
  maxPrice?: number;
  onClose: () => void;
  onFiltersChange: (filters: Partial<SearchFiltersType>) => void;
  onResetFilters: () => void;
  onLocationSelect?: (lat: number, lng: number) => void;
}

interface PetDisplay {
  id: string;
  petName?: string;
  breed?: string | null;
  photoUrl?: string | null;
  selected?: boolean;
}

const SearchFilters: React.FC<SearchFiltersProps> = ({
  filters,
  isOpen,
  maxPrice = 250,
  onClose,
  onFiltersChange,
  onResetFilters,
  onLocationSelect,
}) => {
  const { data: pets, isLoading: isPetsLoading } = useGetPets();

  const petDisplayList: PetDisplay[] = useMemo(
    () =>
      (pets ?? []).map((pet) => ({
        id: pet.id!,
        petName: pet.petName,
        breed: pet.breed,
        photoUrl: pet.photoUrl,
      })),
    [pets]
  );

  useEffect(() => {
    if (petDisplayList.length > 0 && filters.selectedPets.length === 0) {
      onFiltersChange({ selectedPets: [petDisplayList[0].id] });
    }
  }, [petDisplayList]);

  const activeFiltersCount =
    (filters.hasFencedYard ? 1 : 0) + (filters.allowsDogsOnFurniture ? 1 : 0);

  if (!isOpen) return null;

  return (
    <Box
      sx={{
        width: { xs: '100%', md: 320 },
        // height: { xs: 'auto', md: '100vh' },
        height: '100%',
        overflowY: 'auto',
        backgroundColor: 'white',
        borderLeft: { md: '1px solid #E0E0E0' },
        p: 3,
      }}
    >
      {/* Header */}
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          mb: 3,
        }}
      >
        <Typography variant="h6" sx={{ fontWeight: 700 }}>
          Filters
        </Typography>
        <IconButton onClick={onClose} size="small">
          <CloseIcon />
        </IconButton>
      </Box>

      <LocalizationProvider dateAdapter={AdapterDayjs}>
        {/* Service Type */}
        <Box sx={{ mb: 3 }}>
          <Typography variant="body2" sx={{ mb: 1, fontWeight: 600 }}>
            Service type
          </Typography>
          <FormControl fullWidth>
            <Select
              value={filters.serviceType}
              onChange={(e) => onFiltersChange({ serviceType: e.target.value })}
              startAdornment={<LocationOnIcon sx={{ color: '#178B8E', mr: 1, fontSize: 20 }} />}
              sx={{ borderRadius: 2 }}
            >
              {Object.entries(SERVICE_TYPE_LABELS).map(([key, label]) => {
                return (
                  <MenuItem key={key} value={key}>
                    {label}
                  </MenuItem>
                );
              })}
            </Select>
          </FormControl>
        </Box>

        {/* Location */}
        <Box sx={{ mb: 3 }}>
          <Typography variant="body2" sx={{ mb: 1, fontWeight: 600 }}>
            Boarding near
          </Typography>
          <MapboxGeocoderInput
            value={filters.location}
            onSelect={(placeName, lat, lng) => {
              onFiltersChange({ location: placeName });
              onLocationSelect?.(lat, lng);
            }}
            placeholder="Search location..."
          />
        </Box>

        {/* Date Range */}
        <Box sx={{ mb: 3 }}>
          <Typography variant="body2" sx={{ mb: 1, fontWeight: 600 }}>
            Start
          </Typography>
          <DateTimePicker
            value={filters.startDate ? dayjs(filters.startDate) : null}
            onChange={(newValue: Dayjs | null) =>
              onFiltersChange({ startDate: newValue?.toDate() ?? null })
            }
            slotProps={{
              textField: {
                fullWidth: true,
                sx: {
                  '& .MuiOutlinedInput-root': {
                    borderRadius: 2,
                  },
                },
              },
            }}
          />
        </Box>

        <Box sx={{ mb: 3 }}>
          <Typography variant="body2" sx={{ mb: 1, fontWeight: 600 }}>
            End
          </Typography>
          <DateTimePicker
            value={filters.endDate ? dayjs(filters.endDate) : null}
            onChange={(newValue: Dayjs | null) =>
              onFiltersChange({ endDate: newValue?.toDate() ?? null })
            }
            slotProps={{
              textField: {
                fullWidth: true,
                sx: {
                  '& .MuiOutlinedInput-root': {
                    borderRadius: 2,
                  },
                },
              },
            }}
          />
        </Box>

        {/* Your Pet */}
        <SkeletonLoader isLoading={isPetsLoading} count={3}>
          <Box sx={{ mb: 3 }}>
            <Typography variant="body2" sx={{ mb: 2, fontWeight: 600 }}>
              Your Pet:
            </Typography>
            {petDisplayList.map((pet) => (
              <Paper
                key={pet.id}
                sx={{
                  p: 1.5,
                  mb: 1.5,
                  display: 'flex',
                  alignItems: 'center',
                  gap: 1.5,
                  border: filters.selectedPets.includes(pet.id)
                    ? '2px solid #178B8E'
                    : '1px solid #E0E0E0',
                  cursor: 'pointer',
                }}
                onClick={() => {
                  const newSelectedPets = filters.selectedPets.includes(pet.id)
                    ? filters.selectedPets.filter((id) => id !== pet.id)
                    : [...filters.selectedPets, pet.id];
                  onFiltersChange({ selectedPets: newSelectedPets });
                }}
              >
                <Avatar
                  src={pet.photoUrl || ''}
                  sx={{
                    width: 40,
                    height: 40,
                    backgroundColor: '#178B8E',
                  }}
                >
                  {pet?.petName?.charAt(0)}
                </Avatar>
                <Box sx={{ flex: 1 }}>
                  <Typography variant="body2" sx={{ fontWeight: 600 }}>
                    {pet.petName}
                  </Typography>
                  <Typography variant="caption" sx={{ color: '#5A5A5A' }}>
                    {pet.breed}
                  </Typography>
                </Box>
                <Checkbox
                  checked={filters.selectedPets.includes(pet.id)}
                  sx={{
                    color: '#178B8E',
                    '&.Mui-checked': {
                      color: '#178B8E',
                    },
                  }}
                />
              </Paper>
            ))}
          </Box>
        </SkeletonLoader>

        {/* Rate per night */}
        <Box sx={{ mb: 3 }}>
          <Typography variant="body2" sx={{ mb: 1, fontWeight: 600 }}>
            Rate per night
          </Typography>
          <Box sx={{ px: 1 }}>
            <Slider
              value={filters.priceRange}
              onChange={(_, newValue) =>
                onFiltersChange({ priceRange: newValue as [number, number] })
              }
              valueLabelDisplay="auto"
              min={1}
              max={maxPrice}
              sx={{
                color: '#178B8E',
                '& .MuiSlider-thumb': {
                  backgroundColor: '#178B8E',
                },
                '& .MuiSlider-track': {
                  backgroundColor: '#178B8E',
                },
                '& .MuiSlider-rail': {
                  backgroundColor: '#E0E0E0',
                },
              }}
            />
            <Box
              sx={{
                display: 'flex',
                justifyContent: 'space-between',
                mt: 1,
              }}
            >
              <Typography variant="body2" sx={{ fontWeight: 600 }}>
                ${filters.priceRange[0]}
              </Typography>
              <Typography variant="body2" sx={{ fontWeight: 600 }}>
                ${filters.priceRange[1]}
              </Typography>
            </Box>
          </Box>
        </Box>

        {/* Additional Filters */}
        <Box sx={{ mb: 2 }}>
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              mb: 2,
            }}
          >
            <Typography variant="body2" sx={{ fontWeight: 600 }}>
              Filters ({activeFiltersCount})
            </Typography>
            <Button
              variant="text"
              size="small"
              onClick={onResetFilters}
              sx={{
                textTransform: 'none',
                color: '#178B8E',
                fontWeight: 600,
              }}
            >
              Reset All
            </Button>
          </Box>

          <FormControlLabel
            control={
              <Checkbox
                checked={filters.hasFencedYard}
                onChange={(e) => onFiltersChange({ hasFencedYard: e.target.checked })}
                sx={{
                  color: '#178B8E',
                  '&.Mui-checked': {
                    color: '#178B8E',
                  },
                }}
              />
            }
            label={<Typography variant="body2">Has fenced yard</Typography>}
          />
          <FormControlLabel
            control={
              <Checkbox
                checked={filters.allowsDogsOnFurniture}
                onChange={(e) => onFiltersChange({ allowsDogsOnFurniture: e.target.checked })}
                sx={{
                  color: '#178B8E',
                  '&.Mui-checked': {
                    color: '#178B8E',
                  },
                }}
              />
            }
            label={<Typography variant="body2">Dogs allowed on furniture</Typography>}
          />
        </Box>
      </LocalizationProvider>
    </Box>
  );
};

export default SearchFilters;
